import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import leaderboardRibbon from "../images/leaderboard.png";
import "./leaderboard.css";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";

function readStoredUser() {
  try {
    const raw =
      localStorage.getItem("userData") || sessionStorage.getItem("userData") || "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function resolveLobby(locationState) {
  const fromState =
    typeof locationState?.lobbyName === "string" ? locationState.lobbyName.trim() : "";
  if (fromState) return fromState;
  const fromUser =
    typeof locationState?.userLobby === "string" ? locationState.userLobby.trim() : "";
  if (fromUser) return fromUser;
  try {
    const ss = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (ss && ss.trim()) return ss.trim();
  } catch {
    // ignore
  }
  return "";
}

function rowTone(rank) {
  const r = Number(rank);
  if (r === 1) return "tone-1";
  if (r === 2) return "tone-2";
  if (r === 3) return "tone-3";
  return "tone-x";
}

function csvEscape(value) {
  const s = value == null ? "" : String(value);
  const needs = /[",\n\r]/.test(s);
  const escaped = s.replace(/"/g, '""');
  return needs ? `"${escaped}"` : escaped;
}

const pad2 = (n) => String(Math.max(0, Math.trunc(Number(n) || 0))).padStart(2, "0");

/** Normalize DB elapsed time to mm:ss (or hh:mm:ss when needed), e.g. 03:11. */
function formatElapsedTimeForCsv(raw) {
  const cleaned = String(raw ?? "")
    .trim()
    .replace(/\s*(AM|PM)\s*/gi, "")
    .trim();
  if (!cleaned) return "00:00";

  const parts = cleaned.split(":").map((p) => {
    const n = parseInt(String(p).replace(/\D/g, ""), 10);
    return Number.isFinite(n) ? n : 0;
  });

  let hours = 0;
  let minutes = 0;
  let seconds = 0;
  if (parts.length >= 3) {
    hours = parts[0];
    minutes = parts[1];
    seconds = parts[2];
  } else if (parts.length === 2) {
    minutes = parts[0];
    seconds = parts[1];
  } else if (parts.length === 1) {
    seconds = parts[0];
  }

  if (hours > 0) {
    return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
  }
  return `${pad2(minutes)}:${pad2(seconds)}`;
}

function csvEscapeElapsedTime(raw) {
  return csvEscape(formatElapsedTimeForCsv(raw));
}

function downloadTextFile(filename, content, mime = "text/plain;charset=utf-8") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function toBool(v) {
  if (v === true || v === 1) return true;
  const s = String(v ?? "").trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes" || s === "on";
}

/** Points / scoring on for this lobby (from stages). If unknown, show leaderboard. */
function isPointsSystemOn(stage) {
  if (stage == null || typeof stage !== "object") return true;
  return toBool(stage.point_status) || toBool(stage.timer_status);
}

function Leaderboard() {
  const location = useLocation();
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const userData = useMemo(() => readStoredUser(), []);
  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );
  const lobbyName = useMemo(() => {
    const fromState = resolveLobby(location.state);
    if (fromState) return fromState;
    const fromUser = String(userData?.lobby ?? "").trim();
    return fromUser;
  }, [location.state, userData]);
  const leaderboardBackUrl = useMemo(() => {
    const fromUser = String(userData?.backButtonRedirect ?? "").trim();
    if (fromUser && !/\/thankyou\/?$/i.test(fromUser)) return fromUser;
    const fromEnv = String(process.env.REACT_APP_BASE_URL || "").trim();
    if (fromEnv && !/\/thankyou\/?$/i.test(fromEnv)) return fromEnv;
    return "/lobby";
  }, [userData]);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageSettingsLoading, setStageSettingsLoading] = useState(true);
  const [pointsSystemOn, setPointsSystemOn] = useState(true);
  const [error, setError] = useState(null);
  const [isManager, setIsManager] = useState(false);
  const [exportRows, setExportRows] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const currentUserId = String(
    userData.userId ??
      userData.userid ??
      userData.id ??
      userData.user_id ??
      userData.playerId ??
      userData.player_id ??
      ""
  ).trim();

  const normalizeIdentityText = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/[^a-z0-9 ]/g, "");

  const userNameCandidates = useMemo(() => {
    const firstName = String(userData.firstName ?? userData.first_name ?? "").trim();
    const lastName = String(userData.lastName ?? userData.last_name ?? "").trim();
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
    const userId = String(
      userData.userId ??
        userData.userid ??
        userData.id ??
        userData.user_id ??
        userData.playerId ??
        userData.player_id ??
        ""
    ).trim();
    const email = String(userData.email ?? "").trim();
    const emailPrefix = email.includes("@") ? email.split("@")[0].trim() : "";
    const directName = String(userData.name ?? "").trim();

    const raw = [directName, fullName, firstName, lastName, userId, emailPrefix];
    const normalized = raw
      .map(normalizeIdentityText)
      .filter(Boolean)
      .filter((value, idx, arr) => arr.indexOf(value) === idx);
    return normalized;
  }, [userData]);

  const isCurrentUserRow = (row) => {
    const rowUserId = String(row?.userid ?? "").trim();
    if (currentUserId && rowUserId === currentUserId) return true;

    const rowName = normalizeIdentityText(row?.name ?? "");
    if (!rowName || userNameCandidates.length === 0) return false;

    return userNameCandidates.some(
      (candidate) =>
        candidate === rowName ||
        candidate.includes(rowName) ||
        rowName.includes(candidate)
    );
  };

  const visibleRows = useMemo(() => {
    const topFive = list.slice(0, 5).map((row) => ({
      ...row,
      displayRank: row.rank,
    }));
    if (!list.length) return [];

    const matchingByUserId = currentUserId
      ? list.find((row) => String(row?.userid ?? "").trim() === currentUserId)
      : null;
    const matchingByName = list.find((row) => isCurrentUserRow(row));
    const matchingRow = matchingByUserId || matchingByName;

    if (!matchingRow) return topFive;

    const alreadyInTopFive = topFive.some((row) => row.rank === matchingRow.rank);
    if (alreadyInTopFive) return topFive;

    return [
      ...topFive,
      {
        ...matchingRow,
        displayRank: matchingRow.rank,
      },
    ];
  }, [list, currentUserId]);

  useEffect(() => {
    dispatch(setBackButtonUrl(leaderboardBackUrl));
  }, [dispatch, leaderboardBackUrl]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const hadCommon = body.classList.contains("common-bg");
    html.classList.add("leaderboard-static-bg");
    body.classList.add("leaderboard-static-bg");
    body.classList.remove("common-bg");
    return () => {
      html.classList.remove("leaderboard-static-bg");
      body.classList.remove("leaderboard-static-bg");
      if (hadCommon) body.classList.add("common-bg");
    };
  }, []);

  /**
   * Game navigates here with replace:true, so the previous history entry can be /thankyou.
   * Push one state so the first browser Back pops this entry and keeps the same /leaderboard URL.
   */
  useEffect(() => {
    if (typeof window === "undefined" || !window.history?.pushState) return;
    const url = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.history.pushState({ __leaderboardStay: 1 }, "", url);
  }, []);

  useEffect(() => {
    const sessionId = userData.sessionId;
    const organizationId = userData.organizationId;
    const userId =
      userData.userId || userData.userid || userData.id || userData.user_id;
    if (!adminToken || !backendBase || !sessionId || !organizationId) {
      setStageSettingsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setStageSettingsLoading(true);
        const res = await fetch(`${backendBase}/fetchGameQuestions`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            sessionId,
            organizationId,
            lobby: lobbyName || "",
            ...(userId ? { userId } : {}),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.success) {
          setPointsSystemOn(isPointsSystemOn(data.stage));
        } else {
          setPointsSystemOn(true);
        }
      } catch {
        if (!cancelled) setPointsSystemOn(true);
      } finally {
        if (!cancelled) setStageSettingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken, backendBase, lobbyName, userData]);

  useEffect(() => {
    const sessionId = userData.sessionId;
    const organizationId = userData.organizationId;
    if (!adminToken || !backendBase || !sessionId || !organizationId) {
      setLoading(false);
      return;
    }
    if (!pointsSystemOn) {
      setList([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${backendBase}/getLeaderboard`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            sessionId,
            organizationId,
            lobby: lobbyName || "",
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.success && Array.isArray(data.data)) {
          setList(data.data);
          setError(null);
        } else {
          setError(data.message || "Failed to load leaderboard");
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load leaderboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken, backendBase, lobbyName, pointsSystemOn, userData.organizationId, userData.sessionId]);

  useEffect(() => {
    const sessionId = userData.sessionId;
    const organizationId = userData.organizationId;
    const userId =
      userData.userId || userData.userid || userData.id || userData.user_id;
    if (!adminToken || !backendBase || !sessionId || !organizationId || !userId) {
      setIsManager(false);
      setExportRows([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setExportLoading(true);
        const res = await fetch(`${backendBase}/exportStageAnswers`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId,
            sessionId,
            organizationId,
            lobby: lobbyName || "",
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data?.success) throw new Error(data?.message || "Export check failed");
        const m = Boolean(data?.isManager);
        setIsManager(m);
        setExportRows(m && Array.isArray(data?.rows) ? data.rows : []);
      } catch {
        if (!cancelled) {
          setIsManager(false);
          setExportRows([]);
        }
      } finally {
        if (!cancelled) setExportLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken, backendBase, lobbyName, userData]);

  const handleDownloadCsv = () => {
    const header = [
      "Player name",
      "Question number or question name",
      "Selected option",
      "Correct / Incorrect (if scoring enabled)",
      "Time",
    ];

    const lines = [header.map(csvEscape).join(",")];
    for (const stageRow of exportRows) {
      const playerName = String(stageRow?.name ?? "").trim() || "PLAYER";
      const scoringEnabled = Boolean(stageRow?.scoringEnabled);
      const answers = Array.isArray(stageRow?.answers) ? stageRow.answers : [];
      for (let i = 0; i < answers.length; i += 1) {
        const a = answers[i] || {};
        const qLabel =
          String(a.question_name || "").trim() ||
          `Question ${i + 1}`;
        const selected = a.user_answer == null ? "" : String(a.user_answer);
        const correctness = scoringEnabled
          ? (a.is_correct ? "Correct" : "Incorrect")
          : "";
        lines.push(
          [
            csvEscape(playerName),
            csvEscape(qLabel),
            csvEscape(selected),
            csvEscape(correctness),
            csvEscapeElapsedTime(stageRow?.time ?? "00:00"),
          ].join(",")
        );
      }
    }

    const lobbyTag = String(lobbyName || "main").trim().replace(/[^a-z0-9_-]+/gi, "_");
    downloadTextFile(
      `leaderboard_${lobbyTag}.csv`,
      `${lines.join("\n")}\n`,
      "text/csv;charset=utf-8"
    );
  };

  if (stageSettingsLoading) {
    return (
      <div className="leaderboard-page">
        <div className="leaderboard-gridOverlay" aria-hidden />
        <div className="leaderboard-splitOverlay" aria-hidden />
        <div className="leaderboard-top-bar" />
        <div className="lb-thankyou-screen">
          <div className="leaderboard-loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (!pointsSystemOn) {
    return (
      <div className="leaderboard-page">
        <div className="leaderboard-gridOverlay" aria-hidden />
        <div className="leaderboard-splitOverlay" aria-hidden />
        <div className="leaderboard-top-bar" />
        <div className="lb-thankyou-screen">
          <p className="lb-thankyou-message">Thank you for playing</p>
          {isManager ? (
            <button
              type="button"
              className="lb-export-btn lb-export-btn--thankyou"
              onClick={handleDownloadCsv}
              disabled={exportLoading}
            >
              {exportLoading ? "Preparing..." : "Download CSV"}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-gridOverlay" aria-hidden />
      <div className="leaderboard-splitOverlay" aria-hidden />

      <div className="leaderboard-top-bar" />

      <div className="leaderboard-container">
        <div className="lb-card">
          <div className="lb-ribbon">
            <img
              src={leaderboardRibbon}
              alt="Leaderboard"
              className="lb-ribbon-img"
              decoding="async"
            />
          </div>

          <div className="lb-panel">
            {isManager ? (
              <div className="lb-export-row">
                <button
                  type="button"
                  className="lb-export-btn"
                  onClick={handleDownloadCsv}
                  disabled={exportLoading || exportRows.length === 0}
                >
                  {exportLoading ? "Preparing..." : "Download CSV"}
                </button>
              </div>
            ) : null}

            {loading ? (
              <div className="leaderboard-loading">Loading...</div>
            ) : error ? (
              <div className="leaderboard-error">{error}</div>
            ) : visibleRows.length === 0 ? (
              <div className="lb-empty">No players yet.</div>
            ) : (
              <div className="lb-list" role="list" aria-label="Leaderboard">
                <div className="lb-list-header">
                  <div className="lb-row-track">
                    <div className="lb-rank lb-rank--header">
                      <span className="lb-rank-header-text">Rank</span>
                    </div>
                    <div className="lb-bar lb-bar--header">
                      <span className="lb-header-label">Name</span>
                      <span className="lb-header-label">Points</span>
                      <span className="lb-header-label">Time</span>
                    </div>
                  </div>
                </div>
                {visibleRows.map((row) => {
                  const tone = rowTone(row.displayRank ?? row.rank);
                  const isMe = isCurrentUserRow(row);
                  const rankLabel = String(row.displayRank ?? row.rank ?? "");
                  const timeLabel = String(row.time ?? "").trim() || "—";
                  return (
                    <div
                      key={`${row.rank}-${row.name}-${row.points}-${timeLabel}`}
                      className={`lb-row ${tone} ${isMe ? "is-me" : ""}`}
                      role="listitem"
                      aria-label={`Rank ${rankLabel}, ${String(row.name || "").trim() || "Player"}, ${row.points} points, time ${timeLabel}`}
                    >
                      <div className="lb-row-track">
                        <div className={`lb-rank ${tone}`}>{rankLabel}</div>
                        <div className={`lb-bar ${tone}`}>
                          <span className="lb-name">
                            {String(row.name || "").toUpperCase()}
                          </span>
                          <span className="lb-points">{row.points}</span>
                          <span className="lb-time">{timeLabel}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
